/**
 * Features Section
 * Dynamic feature list with templates
 */

import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Plus, X } from 'lucide-react';
import { PackageFormValues } from '@/src/schemas/admin/package-schema';
import { FEATURE_TEMPLATES } from '@/src/constants/admin/packages';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface FeaturesSectionProps {
  form: UseFormReturn<PackageFormValues>;
}

export default function FeaturesSection({ form }: FeaturesSectionProps) {
  const [newFeature, setNewFeature] = useState('');

  const features = form.watch('features') || [];

  const addFeature = () => {
    if (newFeature.trim()) {
      const currentFeatures = form.getValues('features') || [];
      if (!currentFeatures.includes(newFeature.trim())) {
        form.setValue('features', [...currentFeatures, newFeature.trim()]);
        setNewFeature('');
      }
    }
  };

  const removeFeature = (index: number) => {
    const currentFeatures = form.getValues('features') || [];
    form.setValue('features', currentFeatures.filter((_, i) => i !== index));
  };

  const applyTemplate = (templateKey: string) => {
    const template = FEATURE_TEMPLATES[templateKey as keyof typeof FEATURE_TEMPLATES];
    if (template) {
      form.setValue('features', [...template]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Template Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">เทมเพลตคุณสมบัติ</label>
        <Select onValueChange={applyTemplate}>
          <SelectTrigger>
            <SelectValue placeholder="เลือกเทมเพลต (ไม่บังคับ)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="starter">Starter Package</SelectItem>
            <SelectItem value="basic">Basic Package</SelectItem>
            <SelectItem value="premium">Premium Package</SelectItem>
            <SelectItem value="enterprise">Enterprise Package</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          เลือกเทมเพลตเพื่อใส่ฟีเจอร์เริ่มต้น (สามารถแก้ไขได้)
        </p>
      </div>

      {/* Feature Input */}
      <FormField
        control={form.control}
        name="features"
        render={() => (
          <FormItem>
            <FormLabel>รายการคุณสมบัติ *</FormLabel>
            <div className="flex gap-2">
              <FormControl>
                <Input
                  placeholder="เช่น API Integration, Email Support"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addFeature();
                    }
                  }}
                />
              </FormControl>
              <Button type="button" onClick={addFeature}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <FormDescription>
              กดปุ่ม + หรือ Enter เพื่อเพิ่มคุณสมบัติ
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Features List */}
      {features.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">คุณสมบัติที่เพิ่มแล้ว ({features.length})</label>
          <div className="border rounded-lg divide-y">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 hover:bg-muted/50"
              >
                <span className="text-sm">{feature}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFeature(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {features.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">
            ยังไม่มีคุณสมบัติ กรุณาเพิ่มอย่างน้อย 1 รายการ
          </p>
        </div>
      )}
    </div>
  );
}
